import {
    ArrowNewIcon,
    PictureIcon,
    XTwitterIcon
} from "@solomon-apps/shared";
import { CloseIcon } from "@solomon-apps/shared/src/assets/icons/v2/CloseIcon";
import React, { useEffect, useState } from "react";

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageUrl: string | null;
    title: string;
    isLoading: boolean;
    onDownload: () => void;
    onShareX: () => void;
    rateLimitSecondsLeft: number;
}

function formatCountdown(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

export function ShareModal({
    isOpen,
    onClose,
    imageUrl,
    title,
    isLoading,
    onDownload,
    onShareX,
    rateLimitSecondsLeft
}: ShareModalProps): React.ReactElement | null {
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (!isOpen) setImageLoaded(false);
    }, [isOpen]);

    useEffect(() => {
        if (!imageUrl) setImageLoaded(false);
    }, [imageUrl]);

    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e: KeyboardEvent): void => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleEsc);
        return (): void => document.removeEventListener("keydown", handleEsc);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return (): void => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className="relative bg-white shadow-2xl w-full max-w-[640px] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:px-6 sm:py-5">
                    <h2 className="rhymes-title text-3xl md:text-5xl font-normal text-gray-900">
                        Share Stats
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-m-foreground hover:text-m-accent transition-colors cursor-pointer"
                        aria-label="Close"
                    >
                        <CloseIcon className="w-8 h-8" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-3 sm:px-6 sm:py-5">
                    {rateLimitSecondsLeft > 0 ? (
                        <div className="flex items-center justify-center h-[200px]">
                            <p className="text-gray-500 text-center">
                                Too many images generated. Please wait{" "}
                                <span className="tabular-nums">
                                    {formatCountdown(rateLimitSecondsLeft)}
                                </span>{" "}
                                before creating another.
                            </p>
                        </div>
                    ) : isLoading ? (
                        <div className="flex items-center justify-center h-[200px]">
                            <div className="flex items-center gap-3 text-gray-500">
                                <svg
                                    className="animate-spin h-5 w-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        className="opacity-25"
                                    />
                                    <path
                                        d="M4 12a8 8 0 018-8"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <span>Generating card...</span>
                            </div>
                        </div>
                    ) : imageUrl ? (
                        <>
                            {!imageLoaded && (
                                <div className="flex items-center justify-center h-[200px]">
                                    <div className="flex items-center gap-3 text-gray-500">
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                className="opacity-25"
                                            />
                                            <path
                                                d="M4 12a8 8 0 018-8"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span>Loading image...</span>
                                    </div>
                                </div>
                            )}
                            <img
                                src={imageUrl}
                                alt={`${title} share card`}
                                crossOrigin="anonymous"
                                className={`w-full h-auto ${imageLoaded ? "" : "hidden"}`}
                                width={1200}
                                height={630}
                                onLoad={(): void => setImageLoaded(true)}
                            />
                        </>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="p-3 sm:px-6 sm:py-5 flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={onShareX}
                        disabled={
                            !imageUrl || isLoading || rateLimitSecondsLeft > 0
                        }
                        className="flex-1 flex items-center justify-between px-2 py-2 sm:px-4 bg-m-foreground text-m-surface-base font-bold text-base md:text-lg hover:bg-m-accent hover:text-m-foreground transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center gap-2">
                            <XTwitterIcon className="w-8 h-8" />
                            <span>Share on X</span>
                        </div>
                        <ArrowNewIcon className="w-8 h-8" />
                    </button>
                    <button
                        onClick={onDownload}
                        disabled={
                            !imageUrl || isLoading || rateLimitSecondsLeft > 0
                        }
                        className="flex-1 flex items-center justify-between px-2 py-2 sm:px-4 bg-transparent text-m-foreground border-2 border-m-foreground font-bold text-base md:text-lg hover:border-m-accent hover:text-m-accent transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center gap-2">
                            <PictureIcon className="w-8 h-8" />
                            <span>Download Image</span>
                        </div>
                        <ArrowNewIcon className="w-8 h-8" />
                    </button>
                </div>
            </div>
        </div>
    );
}
